import React from 'react';
import { getTeamsContext, getTeamsSettings } from '../../utils';
import { TeamsFrameContainer } from './TeamsFrameContainer';
import { LoadingPage } from '../Shared';
import { IModel, Project, PwWebConection, Service } from '../../types';
import { FeatureLogger } from '../../services';

export interface TabContentProps {
  project: Project;
  service?: Service;
  connection?: PwWebConection;
  defaultRoute?: string;
  iModel?: IModel;
  embedUrl?: string;
  linkToUrl?: string;
  loadStateFromTabData: (context: microsoftTeams.Context) => Promise<any>;
}

/**
 * The entry point to a content tab.
 * Parses entityId for project, service and connection information and
 * fetches service URLs based on above information
 *
 * This could all be collapsed into {@link TeamsFrameContainer} but it's responsible
 * for making the embedded service authentication and communication with it.
 */
export function TabContent(props: TabContentProps): JSX.Element {
  const [embedUrl, setEmbedUrl] = React.useState('');
  const [linkToUrl, setLinkToUrl] = React.useState('');
  const [subEntityId, setSubEntityId] = React.useState('');

  const getEmbedUrl = (context: microsoftTeams.Context) => {
    // Old ProjectWise Web tabs use subEntityId as a frame URL
    return props.service?.displayName === 'ProjectWise Web Connections' &&
      context.subEntityId
      ? context.subEntityId
      : props.embedUrl;
  };

  const getLinkToUrl = (settings: microsoftTeams.settings.Settings) => {
    return props.linkToUrl || settings.websiteUrl;
  };

  React.useEffect(() => {
    (async () => {
      const context = await getTeamsContext();
      await props.loadStateFromTabData(context);
      setSubEntityId(decodeURIComponent(context.subEntityId || ''));
    })().catch(error => console.error(error));
  }, []);

  React.useEffect(() => {
    (async () => {
      const context = await getTeamsContext();
      const settings = await getTeamsSettings();

      setEmbedUrl(decodeURIComponent(getEmbedUrl(context) || ''));
      setLinkToUrl(decodeURIComponent(getLinkToUrl(settings) || ''));

      if (props.service?.displayName) {
        FeatureLogger.markFeature(
          props.project.id,
          'access',
          props.service.displayName,
          props.defaultRoute
        );
      }
    })().catch(error => console.error(error));
  }, [props.embedUrl, props.linkToUrl]);

  return embedUrl ? (
    <TeamsFrameContainer
      projectId={props.project.id}
      frameUrl={embedUrl}
      linkToUrl={linkToUrl}
      connectionName={props.connection?.name}
      connectionId={props.connection?.id}
      serviceName={props.service?.displayName}
      subEntityId={subEntityId}
      iModel={props.iModel}
    />
  ) : (
    <LoadingPage subtext='Adding the final touches...' fullscreen />
  );
}
